import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { postComments, terminateProcess } from './workflow';
import CommonESignWrapper from '../../components/CommonFreyrWrapper';
import { useAppCookies } from '../../assets/utils';

const EndProcessDialog = ({ 
  open, 
  onClose, 
  onEndProcess,
  recordId,
  onSuccess,
  disabled = false
}: { 
  open: boolean, 
  onClose: () => void, 
  onEndProcess: (comment: string) => void,
  recordId: string,
  onSuccess: () => void,
  disabled?: boolean
}) => {

  const [isModified, setIsModified] = useState(false);
  const [comment, setLocalComment] = useState('');
  const [isEsignaturePopupDisplay, setIsEsignaturePopupDisplay] = useState(false);
  const [token, setToken] = useState("");
  const [tenantID, setTenantID] = useState("");
  const [domain, setDomain] = useState("");
  const [userName, setUserName] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const { userID } = useAppCookies();
  const navigate = useNavigate();
  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newComment = event.target.value;
    setLocalComment(newComment);
    setIsModified(newComment.length > 0);
  };

  // Load e-signature configuration on component mount
  useEffect(() => {
    setToken(Cookies.get("ACCESS_TOKEN") || "");
    setTenantID(Cookies.get("USER_TENANT_ID") || "");
    setDomain(Cookies.get("USER_DOMAIN") || "");
    setUserName(Cookies.get("USER_EMAIL") || "");
  }, []);

  const endProcess = () => {
    if (recordId) {
      terminateProcess(recordId, userName, comment, userID)
        .then((res) => {
          if (res.code === 200) {
            enqueueSnackbar(t("workflow.terminateProcessSuccessfully"), {
              variant: "success",
              anchorOrigin: { vertical: "top", horizontal: "center" },
              autoHideDuration: 5000, // Increased from default to 5 seconds m   
              persist: false,
            });
             postComments({
            businessModule: "xEVMPD",
            recordId: "xEVMPD-"+recordId,
            // tags: JSON.stringify(["Plan & Track"]),
            createdBy: userID,
            content: comment,
            contentType: "TEXT",
            sourceType: "Plan&Track",
          }).then(() => {
            console.log("Comment posted successfully");
            setTimeout(()=>{
                window.location.href = "/xevmpd";
            },1000);
          })
        }

        
 
          // Navigate to xevmpd page
           
          
          // Call onSuccess after navigation
          setTimeout(() => {
            onSuccess();
          }, 3500);
        })
        .catch((err) => {
          console.error("Error in terminateProcess:", err);
          // Navigate to xevmpd page even if API fails
          window.location.href = "/xevmpd";
          setTimeout(() => {
            onSuccess();
          }, 500);
        })
        .finally(() => {
          setIsEsignaturePopupDisplay(false);
          setLocalComment("");
          setIsModified(false);
        });
    }
  };

  const handleEndProcess = () => {
    onEndProcess(comment);
    onClose();
    // Open e-signature dialog
    setIsEsignaturePopupDisplay(true);
  };

  const handleSuccessESign = () => {
    // Close e-signature dialog first
    setIsEsignaturePopupDisplay(false);
    
    // Show success message immediately when e-signature is successful
    enqueueSnackbar(t("workflow.terminateProcessSuccessfully"), {
      variant: "success",
      anchorOrigin: { vertical: "top", horizontal: "center" },
      autoHideDuration: 5000, // Increased from 1000ms to 5000ms (5 seconds)
      persist: false,
    });
    
    // Then call endProcess to make API call and navigate
    endProcess();
  };

  const handleErrorESign = async () => {
    setIsEsignaturePopupDisplay(false);
  };

  const handleCloseESign = () => {
    setIsEsignaturePopupDisplay(false);
  };

  const handleSubmitEsign = async () => {
    // This will be called by the e-signature component
    console.log("E-signature submit");
  };

  useEffect(() => {
    if (open) {
      setLocalComment(''); // Reset the comment when the dialog opens
      setIsModified(false);
    }
  }, [open]);
  
  const handleCancel = () => {
    setLocalComment('');
    setIsModified(false);
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={() => {}} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
        sx={{
          '& .MuiDialog-paper': {
            width: '500px',
            maxWidth: '500px'
          }
        }}
         slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(15, 15, 15, 0.4) !important",
          },
        },
      }}
      >
        <DialogTitle sx={{ position: 'relative', paddingRight: '48px' }}>
          <Typography variant="h6">{t("workflow.endProcess")}</Typography>
          <IconButton
            edge="end"
            onClick={handleCancel}
            aria-label="close"
            sx={{
              position: 'absolute',
              right: 30,
              top: 8,
              padding: '4px',
              color: 'rgba(0, 0, 0, 0.54)',
              '&:hover': {
                color: 'rgba(0, 0, 0, 0.87)',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={comment}
            onChange={handleCommentChange}
            inputProps={{ maxLength: 500 }}
            label={<span >{t("comments")} <span style={{ color: 'red' }}>*</span></span>}
            margin="dense"
            InputProps={{
              startAdornment: <Typography style={{ position: 'absolute', right: 0, top: -20 }}>{comment.length}/500</Typography>
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            {t("cancel")}
          </Button>
          <Button 
            onClick={handleEndProcess} 
            disabled={!isModified}
            variant="contained"
            sx={{
              backgroundColor: '#1976d2',
              color: 'white',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              }
            }}
          >
            {t("workflow.end")}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* E-signature Dialog */}
      {isEsignaturePopupDisplay && (
        <CommonESignWrapper
          token={token}
          tenantID={tenantID}
          domain={domain}
          userName={userName}
          isEsignaturePopupDisplay={isEsignaturePopupDisplay}
          handleCloseESign={handleCloseESign}
          handleSubmitEsign={handleSubmitEsign}
          handleSuccessESign={handleSuccessESign}
          handleErrorESign={handleErrorESign}
        />
      )}
    </>
  );
};

export default EndProcessDialog;